import React from 'react';
import { cn } from '@rocco/utils/cn';
import { Input as RoccoInput } from '@rocco/components/input';
import { formatNumberWithCommas } from '@rocco/utils/format-number';
import type { FilterConfigModalItemSimpleRange } from '@rocco/ui/filter-sort/types/filter-config-modal';
import type { RangeFilterState } from '@rocco/ui/filter-sort/view-models/filter-state/range-filter-state';
import type { FilterModalElementProps } from '@rocco/ui/filter-sort/components/filters-modal/FiltersModalElement';

export const FiltersModalSimpleRange = (
    props: FilterModalElementProps<
        FilterConfigModalItemSimpleRange,
        RangeFilterState
    >,
) => {
    const { filterConfigItem, filterState, onFilterStateUpdate } = props;

    if (filterState.range.min === 0 && filterState.range.max === 0) {
        return null;
    }

    const updateRangeChange = (min: number, max: number) => {
        onFilterStateUpdate(
            {
                ...filterState,
                value: { min, max },
            },
            false,
        );
    };

    // Clamp values to the allowed range on blur to prevent search service errors
    const applyRangeChange = () => {
        const clampedMin = Math.max(
            0,
            Math.min(filterState.value.min ?? 0, filterState.range.max),
        );
        const clampedMax = Math.max(
            0,
            Math.min(filterState.value.max ?? 0, filterState.range.max),
        );
        const normalizedMin = Math.min(clampedMin, clampedMax);
        const normalizedMax = Math.max(clampedMin, clampedMax);
        onFilterStateUpdate(
            {
                ...filterState,
                value: { min: normalizedMin, max: normalizedMax },
            },
            true,
        );
    };

    // Compute error state for visual feedback on out-of-range values
    const divisor = filterState.rangeDivisor > 0 ? filterState.rangeDivisor : 1;
    const displayMin = (filterState.value.min ?? 0) / divisor;
    const displayMax = (filterState.value.max ?? 0) / divisor;
    const rangeMin = filterState.range.min / divisor;
    const rangeMax = filterState.range.max / divisor;
    const minError =
        displayMin !== 0 && (displayMin < rangeMin || displayMin > rangeMax);
    const maxError =
        displayMax !== 0 && (displayMax > rangeMax || displayMax < rangeMin);

    const unitPostfix = filterConfigItem.unit ? (
        <span className="text-xs text-gray-400">{filterConfigItem.unit}</span>
    ) : undefined;

    return (
        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
            {filterConfigItem.label ? (
                <span className="min-w-16 font-medium">
                    {filterConfigItem.label}
                </span>
            ) : null}

            <div className="flex flex-wrap gap-1.5">
                <div className="w-[6.875rem] grow">
                    <RoccoInput
                        type="text"
                        placeholder={filterConfigItem.minPlaceholder}
                        postfix={unitPostfix}
                        color="blackOutline"
                        focus="bordered"
                        className={cn(
                            minError && 'text-red-500 ring-1 ring-red-500',
                        )}
                        value={
                            displayMin ? formatNumberWithCommas(displayMin) : ''
                        }
                        onChange={(e) =>
                            updateRangeChange(
                                Number(
                                    e.currentTarget.value.replace(
                                        /[^0-9]/g,
                                        '',
                                    ) || '0',
                                ) * filterState.rangeDivisor,
                                filterState.value.max ?? 0,
                            )
                        }
                        onBlur={applyRangeChange}
                        aria-invalid={minError}
                    />
                </div>

                <div className="flex items-center justify-center">
                    <div className="h-px w-1.5 bg-gray-900" />
                </div>

                <div className="w-[6.875rem] grow">
                    <RoccoInput
                        type="text"
                        placeholder={filterConfigItem.maxPlaceholder}
                        postfix={unitPostfix}
                        color="blackOutline"
                        focus="bordered"
                        className={cn(
                            maxError && 'text-red-500 ring-1 ring-red-500',
                        )}
                        value={
                            displayMax ? formatNumberWithCommas(displayMax) : ''
                        }
                        onChange={(e) =>
                            updateRangeChange(
                                filterState.value.min ?? 0,
                                Number(
                                    e.currentTarget.value.replace(
                                        /[^0-9]/g,
                                        '',
                                    ) || '0',
                                ) * filterState.rangeDivisor,
                            )
                        }
                        onBlur={applyRangeChange}
                        aria-invalid={maxError}
                    />
                </div>
            </div>
        </div>
    );
};
